import { ProductCode } from '@meteoradar/shared/enums/product-code';
import { DEFAULT_SPEED, DEFAULT_WINDOW_HOURS, SPEED_OPTIONS, WINDOW_OPTIONS_HOURS } from '../config.js';

const STORAGE_KEY = 'meteoradar.prefs';

export type Preferences = {
  product: ProductCode;
  windowHours: number;
  speed: number;
};

export const DEFAULT_PREFERENCES: Preferences = {
  product: ProductCode.K02,
  windowHours: DEFAULT_WINDOW_HOURS,
  speed: DEFAULT_SPEED,
};

const isProductCode = (value: unknown): value is ProductCode => typeof value === 'string'
  && (Object.values(ProductCode) as string[]).includes(value);

const pickNumber = (value: unknown, allowed: number[], fallback: number): number => typeof value === 'number' && allowed.includes(value)
  ? value
  : fallback;

export const loadPreferences = (storage: Storage | undefined = globalThis.localStorage): Preferences => {
  const raw = storage?.getItem(STORAGE_KEY);
  if (raw === null || raw === undefined) {
    return { ...DEFAULT_PREFERENCES };
  }
  try {
    const parsed = JSON.parse(raw) as Partial<Record<keyof Preferences, unknown>>;
    return {
      product: isProductCode(parsed.product) ? parsed.product : DEFAULT_PREFERENCES.product,
      windowHours: pickNumber(parsed.windowHours, WINDOW_OPTIONS_HOURS, DEFAULT_PREFERENCES.windowHours),
      speed: pickNumber(parsed.speed, SPEED_OPTIONS, DEFAULT_PREFERENCES.speed),
    };
  } catch {
    return { ...DEFAULT_PREFERENCES };
  }
};

export const savePreferences = (prefs: Preferences, storage: Storage | undefined = globalThis.localStorage): void => {
  storage?.setItem(STORAGE_KEY, JSON.stringify(prefs));
};

export const readUrlOverrides = (search: string): Partial<Preferences> => {
  const params = new URLSearchParams(search);
  const overrides: Partial<Preferences> = {};
  const product = params.get('p');
  if (isProductCode(product)) {
    overrides.product = product;
  }
  const hours = Number(params.get('h'));
  if (WINDOW_OPTIONS_HOURS.includes(hours)) {
    overrides.windowHours = hours;
  }
  return overrides;
};

export const buildUrlSearch = (prefs: Pick<Preferences, 'product' | 'windowHours'>): string => {
  const params = new URLSearchParams();
  params.set('p', prefs.product);
  params.set('h', String(prefs.windowHours));
  return `?${params.toString()}`;
};
